import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import { trackException } from '../config/appInsights';
import { ThemeProviderWrapper } from '../features/appTheme';
import { AuthProvider } from '../features/authentication';
import Status500 from '../pages/Fallbacks/Status/Status500/Status500';
import { themeNames, useThemeStore } from '../store/theme/themeStore';
import { SidebarProvider } from './SidebarContext';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AppProviders component serves as the top-level provider for the application.
 * It wraps the entire application with various context providers and essential
 * components for consistent theming, authentication, routing, and error handling.
 *
 * @component
 * @param {AppProvidersProps} children - The child components to be wrapped by the providers.
 * @return {JSX.Element} The wrapped child components with required providers and error boundaries.
 */
const AppProviders = ({ children }: AppProvidersProps) => {
  const theme = useThemeStore((state) => state.theme);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-za">
      <ThemeProviderWrapper>
        <AuthProvider>
          <SidebarProvider>
            <HelmetProvider>
              <ErrorBoundary
                onError={(error) => trackException(error)}
                fallbackRender={({ resetErrorBoundary }) => (
                  <Status500 resetErrorBoundary={resetErrorBoundary} />
                )}
              >
                <CssBaseline />
                <ToastContainer
                  theme={theme === themeNames.dark ? 'dark' : 'light'}
                />
                {children}
              </ErrorBoundary>
            </HelmetProvider>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProviderWrapper>
    </LocalizationProvider>
  );
};

export default AppProviders;
